import type WebApiClient from "../../api/WebApiClient.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { type Gene } from "@/graphql.ts";
import { LOAD_GENE_FAILURE, LOAD_GENE_REQUEST, LOAD_GENE_SUCCESS } from "@/redux/action-types.ts";

/**
 * State definition
 */
export interface IGeneState {
  gene?: Gene;
  isLoading: boolean;
}

const initialState: IGeneState = {
  isLoading: false,
  gene: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_GENE_REQUEST]: (state: IGeneState, _payload: Gene): IGeneState => {
    return { ...state, isLoading: true };
  },
  [LOAD_GENE_SUCCESS]: (state: IGeneState, payload: Gene): IGeneState => {
    return { ...state, isLoading: false, gene: payload };
  },
  [LOAD_GENE_FAILURE]: (state: IGeneState, _payload): IGeneState => {
    return { ...state, isLoading: false, gene: null };
  },
});

/**
 * Action creators
 */
let lastFetchedGeneId = -1;

export function shouldLoadGene(geneId: number) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<Gene> => {
    const state: IAppState = getState();
    lastFetchedGeneId = geneId;

    // fetch the gene only if no loading is currently in progress and gene is either absent or doesn't match the gene ID
    if (!state.gene.gene || state.gene.gene.id !== geneId) {
      dispatch({ type: LOAD_GENE_REQUEST });

      return webApiClient
        .fetchGene(geneId)
        .then((response: Gene) => {
          if (lastFetchedGeneId === response.id) {
            dispatch({
              type: LOAD_GENE_SUCCESS,
              payload: response,
            });
            return response;
          }

          return state.gene.gene;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_GENE_FAILURE,
            alertTitle: "Fetching of Gene Failed",
          }),
        );
    } else {
      // We reset the loading flag to not stay on a loading state indefinitely
      // because lastFetchId can be not synchronized from state.gene.gene.id

      // TODO, probably need to change props.selectedItem
      dispatch({
        type: LOAD_GENE_SUCCESS,
        payload: state.gene.gene,
      });

      return Promise.resolve(state.gene.gene);
    }
  };
}
